/** DIVETTA MILANO R7 — Editorial route state/lifecycle controller. */
(() => {
  'use strict';
  const KEY = '__DivettaR7Editorial';
  window[KEY]?.destroy?.();

  const globalController = new AbortController();
  const { signal } = globalController;
  const desktop = window.matchMedia('(min-width: 990px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mounted = new Map();
  const partCache = new WeakMap();
  const stateCache = new WeakMap();
  let frame = 0;
  let unsubscribeFrame = null;

  const clamp = (value) => Math.min(1, Math.max(0, value));
  const progressFor = (node) => {
    const rect = node.getBoundingClientRect();
    return clamp(-rect.top / Math.max(1, rect.height - window.innerHeight));
  };
  const enhance = () => document.documentElement.classList.toggle('r7-editorial-enhanced', desktop.matches && !reduceMotion.matches && (window.DivettaZorge?.canAnimate ?? true));
  const revealEditorTarget = (node) => {
    if (!(node instanceof Element)) return;
    const rect = node.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) node.scrollIntoView({ behavior: 'auto', block: 'center' });
  };

  const parts = (root) => {
    if (!partCache.has(root)) partCache.set(root, {
      states: Array.from(root.querySelectorAll('[data-r7-editorial-state]')),
      buttons: Array.from(root.querySelectorAll('[data-r7-editorial-state-button]')),
      current: root.querySelector('[data-r7-editorial-current]'),
      progress: root.querySelector('[data-r7-editorial-progress]')
    });
    return partCache.get(root);
  };

  const setState = (root, index) => {
    const { states, buttons, current, progress } = parts(root);
    if (!states.length) return;
    const next = Math.max(0, Math.min(states.length - 1, Number(index) || 0));
    const enhanced = document.documentElement.classList.contains('r7-editorial-enhanced');
    const stateKey = `${next}:${enhanced}`;
    if (stateCache.get(root) === stateKey) return;
    stateCache.set(root, stateKey);
    root.dataset.r7EditorialActive = String(next);
    states.forEach((state, stateIndex) => {
      const active = stateIndex === next;
      state.classList.toggle('is-active', active);
      if (enhanced) {
        state.toggleAttribute('inert', !active);
        state.setAttribute('aria-hidden', active ? 'false' : 'true');
      } else {
        state.removeAttribute('inert');
        state.removeAttribute('aria-hidden');
      }
    });
    buttons.forEach((button, buttonIndex) => button.setAttribute('aria-pressed', buttonIndex === next ? 'true' : 'false'));
    if (current) current.textContent = String(next + 1).padStart(2, '0');
    const stateProgress = states.length > 1 ? next / (states.length - 1) : 1;
    root.style.setProperty('--r7-editorial-state-progress', Math.max(.01, stateProgress).toFixed(4));
    if (progress) progress.style.transform = `scaleX(${Math.max(.01, stateProgress).toFixed(4)})`;
  };

  const scrollToState = (root, index) => {
    const { states } = parts(root);
    const next = Math.max(0, Math.min(states.length - 1, Number(index) || 0));
    if (!document.documentElement.classList.contains('r7-editorial-enhanced')) {
      states[next]?.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
      return;
    }
    const story = root.querySelector('.r7-maison-map__story') || root;
    const rect = story.getBoundingClientRect();
    const start = window.scrollY + rect.top;
    const travel = Math.max(0, rect.height - window.innerHeight);
    const ratio = states.length > 1 ? (next + .25) / states.length : 0;
    window.scrollTo({ top: start + travel * ratio, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  };

  const mountStory = (root) => {
    if (mounted.has(root)) return;
    const controller = new AbortController();
    const { states, buttons } = parts(root);
    if (!states.length) return;
    buttons.forEach((button) => button.addEventListener('click', () => scrollToState(root, button.dataset.r7EditorialStateButton), { signal: controller.signal }));
    setState(root, 0);
    mounted.set(root, controller);
  };

  const mount = (scope = document) => {
    enhance();
    const roots = scope.matches?.('[data-r7-editorial-story]') ? [scope] : Array.from(scope.querySelectorAll?.('[data-r7-editorial-story]') || []);
    roots.forEach(mountStory);
    requestUpdate();
  };

  const unmount = (scope) => {
    Array.from(mounted.keys()).forEach((root) => {
      if (root === scope || scope.contains?.(root)) {
        mounted.get(root)?.abort();
        mounted.delete(root);
        parts(root).states.forEach((state) => { state.removeAttribute('inert'); state.removeAttribute('aria-hidden'); });
        partCache.delete(root); stateCache.delete(root);
      }
    });
  };

  const update = () => {
    frame = 0;
    Array.from(mounted.keys()).filter((root) => root.isConnected).map((root) => {
      const story = root.querySelector('.r7-maison-map__story') || root;
      return { root, measuredProgress: progressFor(story) };
    }).forEach(({ root, measuredProgress }) => {
      const { states } = parts(root);
      if (!states.length) return;
      if (!document.documentElement.classList.contains('r7-editorial-enhanced')) {
        setState(root, root.dataset.r7EditorialActive || 0);
        return;
      }
      const story = root.querySelector('.r7-maison-map__story') || root;
      const forced = root.dataset.r7EditorIndex;
      const progress = measuredProgress;
      const index = forced !== undefined ? Number(forced) : Math.min(states.length - 1, Math.floor(progress * states.length + .0001));
      setState(root, index);
    });
  };
  const requestUpdate = () => {
    if (window.DivettaZorge?.requestFrame) { window.DivettaZorge.requestFrame(); return; }
    if (!frame) frame = window.requestAnimationFrame(update);
  };
  const viewportChange = () => { enhance(); document.querySelectorAll('[data-r7-editorial-story]').forEach((root) => setState(root, root.dataset.r7EditorialActive || 0)); requestUpdate(); };

  if (window.DivettaZorge?.subscribe) unsubscribeFrame = window.DivettaZorge.subscribe('editorial', update);
  else window.addEventListener('scroll', requestUpdate, { passive:true, signal });
  window.addEventListener('resize', viewportChange, { passive:true, signal });
  desktop.addEventListener?.('change', viewportChange, { signal });
  reduceMotion.addEventListener?.('change', viewportChange, { signal });
  document.addEventListener('shopify:section:load', (event) => mount(event.target), { signal });
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target), { signal });
  document.addEventListener('shopify:section:select', (event) => { mount(event.target); revealEditorTarget(event.target); }, { signal });
  document.addEventListener('shopify:block:select', (event) => {
    const state = event.target.closest?.('[data-r7-editorial-state]');
    const root = state?.closest?.('[data-r7-editorial-story]');
    if (!state || !root) return;
    root.dataset.r7EditorIndex = state.dataset.r7EditorialStateIndex || '0';
    setState(root, root.dataset.r7EditorIndex);
    revealEditorTarget(root);
  }, { signal });
  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest?.('[data-r7-editorial-story]');
    if (root) delete root.dataset.r7EditorIndex;
    requestUpdate();
  }, { signal });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once:true, signal });
  else mount(document);

  window[KEY] = { mount, destroy() { globalController.abort(); unsubscribeFrame?.(); if (frame) cancelAnimationFrame(frame); mounted.forEach((controller, root) => { controller.abort(); parts(root).states.forEach((state) => { state.removeAttribute('inert'); state.removeAttribute('aria-hidden'); }); }); mounted.clear(); document.documentElement.classList.remove('r7-editorial-enhanced'); delete window[KEY]; } };
})();
